import React from 'react';
import { motion } from 'framer-motion';

// Template 1: Professional/Corporate
export const ProfessionalTemplate = ({ resumeData }) => {
  // Debug: Log what data the Professional template is receiving
  console.log('📄 ProfessionalTemplate received data:', resumeData);
  console.log('📄 Professional skills:', resumeData?.skills);
  console.log('📄 Professional name:', resumeData?.name);

  return (
    <div className="bg-white p-8 shadow-lg max-w-4xl mx-auto" style={{ minHeight: '11in' }}>
      {/* Header */}
      <div className="border-b-2 border-blue-600 pb-4 mb-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">{resumeData?.name || 'John Doe'}</h1>
            <p className="text-xl text-blue-600 mb-2">{resumeData?.title || 'Professional Title'}</p>
            <div className="flex flex-wrap gap-4 text-gray-700">
              <span>{resumeData?.email || 'email@example.com'}</span>
              <span>{resumeData?.phone || '(555) 123-4567'}</span>
              <span>{resumeData?.location || 'City, State'}</span>
            </div>
          </div>
          {/* Photo */}
          {resumeData?.photo && (
            <div className="ml-6">
              <img 
                src={resumeData.photo} 
                alt="Profile" 
                className="w-24 h-24 rounded-full object-cover border-4 border-blue-600"
              />
            </div>
          )}
        </div>
      </div>

      {/* Summary */}
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-gray-900 mb-3 border-l-4 border-blue-600 pl-3">
          Professional Summary
        </h2>
        <p className="text-gray-700 leading-relaxed">
          {resumeData?.summary || 'Experienced professional with expertise in multiple domains...'}
        </p>
      </div>

      {/* Skills */}
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-gray-900 mb-3 border-l-4 border-blue-600 pl-3">
          Core Competencies
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {(resumeData?.skills || ['JavaScript', 'React', 'Node.js', 'Python', 'SQL', 'AWS']).map((skill, index) => (
            <div key={index} className="bg-gray-100 px-3 py-1 rounded text-sm">
              {skill}
            </div>
          ))}
        </div>
      </div>

      {/* Experience */}
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-gray-900 mb-3 border-l-4 border-blue-600 pl-3">
          Professional Experience
        </h2>
        {(resumeData?.experience || [
          {
            title: 'Senior Software Engineer',
            company: 'Tech Corp',
            period: '2020 - Present',
            achievements: [
              'Led development of microservices architecture resulting in 40% performance improvement',
              'Mentored team of 5 junior developers and improved code quality by 35%',
              'Implemented CI/CD pipeline reducing deployment time by 60%'
            ]
          }
        ]).map((exp, index) => (
          <div key={index} className="mb-4">
            <div className="flex justify-between items-start mb-2">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{exp.title}</h3>
                <p className="text-blue-600 font-medium">{exp.company}</p>
              </div>
              <span className="text-gray-600 text-sm">{exp.period}</span>
            </div>
            <ul className="list-disc list-inside text-gray-700 space-y-1">
              {exp.achievements.map((achievement, i) => (
                <li key={i}>{achievement}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Education */}
      <div>
        <h2 className="text-2xl font-semibold text-gray-900 mb-3 border-l-4 border-blue-600 pl-3">
          Education
        </h2>
        <div className="space-y-4">
          {Array.isArray(resumeData.education) ? (
            resumeData.education.map((edu, index) => (
              <div key={index} className="flex justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {edu.degree} {edu.field ? `in ${edu.field}` : ''}
                  </h3>
                  <p className="text-blue-600">{edu.school}</p>
                </div>
                <span className="text-gray-600">{edu.year}</span>
              </div>
            ))
          ) : (
            <div className="flex justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">{resumeData.education?.degree || 'Bachelor of Science in Computer Science'}</h3>
                <p className="text-blue-600">{resumeData.education?.school || 'University Name'}</p>
              </div>
              <span className="text-gray-600">{resumeData.education?.year || '2018'}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Template 2: Creative/Modern
export const CreativeTemplate = ({ resumeData }) => {
  // Debug: Log what data the Creative template is receiving
  console.log('🎨 CreativeTemplate received data:', resumeData);
  console.log('🎨 CreativeTemplate skills:', resumeData?.skills);
  console.log('🎨 CreativeTemplate name:', resumeData?.name);

  return (
    <div className="bg-gradient-to-br from-purple-50 to-blue-50 p-8 max-w-4xl mx-auto" style={{ minHeight: '11in' }}>
      <div className="bg-white rounded-3xl shadow-2xl p-8">
        {/* Header */}
        <div className="text-center mb-8">
          {/* Photo or Initial */}
          {resumeData?.photo ? (
            <img 
              src={resumeData.photo} 
              alt="Profile" 
              className="w-24 h-24 rounded-full mx-auto mb-4 object-cover border-4 border-purple-600 shadow-lg"
            />
          ) : (
            <div className="w-24 h-24 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full mx-auto mb-4 flex items-center justify-center text-white text-3xl font-bold shadow-lg">
              {(resumeData?.name || 'JD').charAt(0)}
            </div>
          )}
          <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent mb-2">
            {resumeData?.name || 'John Doe'}
          </h1>
          <p className="text-xl text-gray-600 mb-4">{resumeData?.title || 'Creative Professional'}</p>
          <div className="flex justify-center space-x-6 text-gray-600">
            <span>{resumeData?.email || 'email@example.com'}</span>
            <span>{resumeData?.phone || '(555) 123-4567'}</span>
            <span>{resumeData?.location || 'City, State'}</span>
          </div>
        </div>

        {/* Summary */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">About Me</h2>
          <p className="text-gray-700 text-center leading-relaxed max-w-3xl mx-auto">
            {resumeData?.summary || 'Creative and innovative professional with a passion for delivering exceptional results...'}
          </p>
        </div>

        {/* Skills */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">Skills & Expertise</h2>
          <div className="flex flex-wrap justify-center gap-3">
            {(resumeData?.skills || ['Design', 'Development', 'Strategy', 'Leadership', 'Innovation', 'Problem Solving']).map((skill, index) => (
              <span key={index} className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                {skill}
              </span>
            ))}
          </div>
        </div>

        {/* Experience */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Experience Journey</h2>
          <div className="space-y-6">
            {(resumeData?.experience || [
              {
                title: 'Creative Director',
                company: 'Design Studio',
                period: '2020 - Present',
                achievements: [
                  'Increased client satisfaction by 50% through innovative design solutions',
                  'Led creative team of 8 designers and developers',
                  'Won 3 industry awards for outstanding project deliverables'
                ]
              }
            ]).map((exp, index) => (
              <div key={index} className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl p-6">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{exp.title}</h3>
                    <p className="text-purple-600 font-semibold">{exp.company}</p>
                  </div>
                  <span className="bg-white px-3 py-1 rounded-full text-sm text-gray-600">{exp.period}</span>
                </div>
                <ul className="list-none space-y-2">
                  {exp.achievements.map((achievement, i) => (
                    <li key={i} className="flex items-start">
                      <span className="w-2 h-2 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span className="text-gray-700">{achievement}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Education */}
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Education</h2>
          <div className="space-y-4">
            {Array.isArray(resumeData.education) ? (
              resumeData.education.map((edu, index) => (
                <div key={index} className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl p-6">
                  <h3 className="font-bold text-gray-900 text-lg">
                    {edu.degree} {edu.field ? `in ${edu.field}` : ''}
                  </h3>
                  <p className="text-purple-600 font-semibold">{edu.school}</p>
                  <span className="text-gray-600">{edu.year}</span>
                </div>
              ))
            ) : (
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl p-6">
                <h3 className="font-bold text-gray-900 text-lg">{resumeData.education?.degree || 'Bachelor of Arts in Design'}</h3>
                <p className="text-purple-600 font-semibold">{resumeData.education?.school || 'University Name'}</p>
                <span className="text-gray-600">{resumeData.education?.year || '2018'}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Template 3: Minimalist/Clean
export const MinimalistTemplate = ({ resumeData }) => {
  // Debug: Log what data the Minimalist template is receiving
  console.log('✨ MinimalistTemplate received data:', resumeData);
  console.log('✨ Minimalist skills:', resumeData?.skills);
  console.log('✨ Minimalist name:', resumeData?.name);

  return (
    <div className="bg-white p-8 max-w-4xl mx-auto font-light" style={{ minHeight: '11in' }}>
      {/* Header */}
      <div className="border-b border-gray-200 pb-8 mb-8">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h1 className="text-5xl font-thin text-gray-900 mb-2">{resumeData?.name || 'John Doe'}</h1>
            <p className="text-lg text-gray-600 mb-4">{resumeData?.title || 'Professional'}</p>
            <div className="flex space-x-8 text-gray-600">
              <span>{resumeData?.email || 'email@example.com'}</span>
              <span>{resumeData?.phone || '(555) 123-4567'}</span>
              <span>{resumeData?.location || 'City, State'}</span>
            </div>
          </div>
          {/* Photo */}
          {resumeData?.photo && (
            <div className="ml-8">
              <img 
                src={resumeData.photo} 
                alt="Profile" 
                className="w-20 h-20 rounded-full object-cover border border-gray-300"
              />
            </div>
          )}
        </div>
      </div>

      {/* Summary */}
      <div className="mb-8">
        <p className="text-gray-700 leading-loose text-lg">
          {resumeData?.summary || 'Dedicated professional focused on delivering exceptional results through innovative solutions and collaborative leadership...'}
        </p>
      </div>

      {/* Experience */}
      <div className="mb-8">
        <h2 className="text-2xl font-thin text-gray-900 mb-6 uppercase tracking-wide">Experience</h2>
        {(resumeData?.experience || [
          {
            title: 'Senior Consultant',
            company: 'Consulting Firm',
            period: '2020 - Present',
            achievements: [
              'Delivered strategic solutions for Fortune 500 clients',
              'Managed cross-functional teams across multiple time zones',
              'Achieved 95% client retention rate through exceptional service delivery'
            ]
          }
        ]).map((exp, index) => (
          <div key={index} className="mb-6 pb-6 border-b border-gray-100 last:border-b-0">
            <div className="flex justify-between items-baseline mb-3">
              <div>
                <h3 className="text-xl font-normal text-gray-900">{exp.title}</h3>
                <p className="text-gray-600">{exp.company}</p>
              </div>
              <span className="text-gray-500 text-sm">{exp.period}</span>
            </div>
            <ul className="list-none space-y-2">
              {exp.achievements.map((achievement, i) => (
                <li key={i} className="text-gray-700 leading-relaxed">
                  {achievement}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Skills */}
      <div className="mb-8">
        <h2 className="text-2xl font-thin text-gray-900 mb-6 uppercase tracking-wide">Skills</h2>
        <div className="grid grid-cols-3 gap-4">
          {(resumeData?.skills || ['Strategy', 'Leadership', 'Analysis', 'Communication', 'Innovation', 'Management']).map((skill, index) => (
            <div key={index} className="text-gray-700">
              {skill}
            </div>
          ))}
        </div>
      </div>

      {/* Education */}
      <div>
        <h2 className="text-2xl font-thin text-gray-900 mb-6 uppercase tracking-wide">Education</h2>
        <div className="space-y-4">
          {Array.isArray(resumeData.education) ? (
            resumeData.education.map((edu, index) => (
              <div key={index} className="flex justify-between">
                <div>
                  <h3 className="font-normal text-gray-900">
                    {edu.degree} {edu.field ? `in ${edu.field}` : ''}
                  </h3>
                  <p className="text-gray-600">{edu.school}</p>
                </div>
                <span className="text-gray-500">{edu.year}</span>
              </div>
            ))
          ) : (
            <div className="flex justify-between">
              <div>
                <h3 className="font-normal text-gray-900">{resumeData.education?.degree || 'Master of Business Administration'}</h3>
                <p className="text-gray-600">{resumeData.education?.school || 'University Name'}</p>
              </div>
              <span className="text-gray-500">{resumeData.education?.year || '2018'}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Template 4: Executive
export const ExecutiveTemplate = ({ resumeData }) => {
  return (
    <div className="bg-white p-8 shadow-lg max-w-4xl mx-auto border-l-8 border-gray-800" style={{ minHeight: '11in' }}>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h1 className="text-5xl font-bold text-gray-900 mb-1">{resumeData?.name || 'John Doe'}</h1>
            <p className="text-2xl text-gray-600 mb-3 uppercase tracking-wider">{resumeData?.title || 'Executive Leader'}</p>
            <div className="bg-gray-800 h-1 w-24 mb-4"></div>
            <div className="space-y-1 text-gray-700">
              <div>{resumeData?.email || 'email@example.com'}</div>
              <div>{resumeData?.phone || '(555) 123-4567'}</div>
              <div>{resumeData?.location || 'City, State'}</div>
            </div>
          </div>
          {resumeData?.photo && (
            <div className="ml-8">
              <img 
                src={resumeData.photo} 
                alt="Profile" 
                className="w-32 h-32 rounded object-cover border-4 border-gray-800"
              />
            </div>
          )}
        </div>
      </div>

      {/* Executive Summary */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 uppercase tracking-wide border-b-2 border-gray-800 pb-2">
          Executive Summary
        </h2>
        <p className="text-gray-700 leading-relaxed text-lg">
          {resumeData?.summary || 'Visionary executive with proven track record of driving organizational growth and transformation...'}
        </p>
      </div>

      {/* Core Competencies */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 uppercase tracking-wide border-b-2 border-gray-800 pb-2">
          Core Competencies
        </h2>
        <div className="grid grid-cols-2 gap-4">
          {(resumeData?.skills || ['Strategic Planning', 'Leadership', 'Operations', 'Finance', 'M&A', 'Growth Strategy']).map((skill, index) => (
            <div key={index} className="flex items-center">
              <div className="w-2 h-2 bg-gray-800 rounded-full mr-3"></div>
              <span className="text-gray-700 font-medium">{skill}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Experience */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 uppercase tracking-wide border-b-2 border-gray-800 pb-2">
          Executive Experience
        </h2>
        {(resumeData?.experience || [
          {
            title: 'Chief Executive Officer',
            company: 'Fortune 500 Company',
            period: '2018 - Present',
            achievements: ['Led company to 300% revenue growth', 'Expanded operations to 15 countries', 'Managed 5000+ employee organization']
          }
        ]).map((exp, index) => (
          <div key={index} className="mb-6 pb-6 border-b border-gray-200 last:border-b-0">
            <div className="flex justify-between items-baseline mb-3">
              <div>
                <h3 className="text-xl font-bold text-gray-900">{exp.title}</h3>
                <p className="text-lg text-gray-600 font-medium">{exp.company}</p>
              </div>
              <span className="text-gray-500 font-medium">{exp.period}</span>
            </div>
            <ul className="space-y-2">
              {exp.achievements.map((achievement, i) => (
                <li key={i} className="text-gray-700 flex items-start">
                  <span className="w-1.5 h-1.5 bg-gray-800 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  {achievement}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

// Template 5: Modern
export const ModernTemplate = ({ resumeData }) => {
  return (
    <div className="bg-gray-50 p-8 max-w-4xl mx-auto" style={{ minHeight: '11in' }}>
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 p-8 text-white">
          <div className="flex items-center">
            {resumeData?.photo ? (
              <img 
                src={resumeData.photo} 
                alt="Profile" 
                className="w-24 h-24 rounded-full object-cover border-4 border-white mr-6"
              />
            ) : (
              <div className="w-24 h-24 rounded-full bg-white/20 flex items-center justify-center text-3xl font-bold mr-6">
                {(resumeData?.name || 'JD').charAt(0)}
              </div>
            )}
            <div className="flex-1">
              <h1 className="text-4xl font-bold mb-2">{resumeData?.name || 'John Doe'}</h1>
              <p className="text-xl opacity-90 mb-3">{resumeData?.title || 'Modern Professional'}</p>
              <div className="flex flex-wrap gap-4 text-sm opacity-80">
                <span>{resumeData?.email || 'email@example.com'}</span>
                <span>{resumeData?.phone || '(555) 123-4567'}</span>
                <span>{resumeData?.location || 'City, State'}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-8">
          {/* About */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <div className="w-1 h-6 bg-gradient-to-b from-indigo-500 to-purple-500 rounded-full mr-3"></div>
              About Me
            </h2>
            <p className="text-gray-700 leading-relaxed">
              {resumeData?.summary || 'Innovative professional passionate about leveraging modern technologies to drive exceptional results...'}
            </p>
          </div>

          {/* Skills */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <div className="w-1 h-6 bg-gradient-to-b from-purple-500 to-pink-500 rounded-full mr-3"></div>
              Skills
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {(resumeData?.skills || ['React', 'Design Systems', 'Leadership', 'Analytics', 'Cloud', 'AI/ML']).map((skill, index) => (
                <div key={index} className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg px-4 py-2 text-center text-sm font-medium text-gray-800">
                  {skill}
                </div>
              ))}
            </div>
          </div>

          {/* Experience */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <div className="w-1 h-6 bg-gradient-to-b from-pink-500 to-red-500 rounded-full mr-3"></div>
              Experience
            </h2>
            {(resumeData?.experience || [
              {
                title: 'Senior Product Manager',
                company: 'Tech Startup',
                period: '2020 - Present',
                achievements: ['Launched 3 successful products', 'Grew user base by 400%', 'Led cross-functional team of 12']
              }
            ]).map((exp, index) => (
              <div key={index} className="mb-6 p-6 bg-gray-50 rounded-xl border-l-4 border-gradient-to-b from-indigo-500 to-purple-500">
                <div className="flex justify-between items-baseline mb-3">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{exp.title}</h3>
                    <p className="text-lg text-indigo-600 font-medium">{exp.company}</p>
                  </div>
                  <span className="text-gray-500 font-medium bg-white px-3 py-1 rounded-full text-sm">{exp.period}</span>
                </div>
                <ul className="space-y-2">
                  {exp.achievements.map((achievement, i) => (
                    <li key={i} className="text-gray-700 flex items-start">
                      <span className="w-2 h-2 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      {achievement}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Template 6: Elegant
export const ElegantTemplate = ({ resumeData }) => {
  return (
    <div className="bg-gradient-to-br from-rose-50 to-amber-50 p-8 max-w-4xl mx-auto" style={{ minHeight: '11in' }}>
      <div className="bg-white p-8 rounded-lg shadow-sm border border-rose-200">
        {/* Header */}
        <div className="text-center mb-8 pb-8 border-b-2 border-rose-200">
          {resumeData?.photo ? (
            <img 
              src={resumeData.photo} 
              alt="Profile" 
              className="w-24 h-24 rounded-full object-cover border-4 border-rose-300 mx-auto mb-4"
            />
          ) : (
            <div className="w-24 h-24 rounded-full bg-gradient-to-r from-rose-300 to-amber-300 mx-auto mb-4 flex items-center justify-center text-white text-2xl font-bold">
              {(resumeData?.name || 'JD').charAt(0)}
            </div>
          )}
          <h1 className="text-4xl font-serif text-gray-900 mb-2">{resumeData?.name || 'John Doe'}</h1>
          <p className="text-xl text-rose-600 mb-4 italic">{resumeData?.title || 'Elegant Professional'}</p>
          <div className="flex justify-center space-x-6 text-gray-600">
            <span>{resumeData?.email || 'email@example.com'}</span>
            <span>•</span>
            <span>{resumeData?.phone || '(555) 123-4567'}</span>
            <span>•</span>
            <span>{resumeData?.location || 'City, State'}</span>
          </div>
        </div>

        {/* Professional Summary */}
        <div className="mb-8">
          <h2 className="text-2xl font-serif text-gray-900 mb-4 text-center">Professional Summary</h2>
          <div className="border-l-4 border-rose-300 pl-6">
            <p className="text-gray-700 leading-relaxed italic text-lg">
              {resumeData?.summary || 'Accomplished professional with an elegant approach to delivering sophisticated solutions and exceptional client experiences...'}
            </p>
          </div>
        </div>

        {/* Expertise */}
        <div className="mb-8">
          <h2 className="text-2xl font-serif text-gray-900 mb-4 text-center">Areas of Expertise</h2>
          <div className="grid grid-cols-2 gap-4">
            {(resumeData?.skills || ['Strategic Consulting', 'Client Relations', 'Project Management', 'Business Development', 'Process Optimization', 'Team Leadership']).map((skill, index) => (
              <div key={index} className="text-center">
                <div className="bg-rose-50 border border-rose-200 rounded-lg px-4 py-3">
                  <span className="text-gray-800 font-medium">{skill}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Professional Experience */}
        <div className="mb-8">
          <h2 className="text-2xl font-serif text-gray-900 mb-6 text-center">Professional Experience</h2>
          {(resumeData?.experience || [
            {
              title: 'Senior Consultant',
              company: 'Prestigious Consulting Firm',
              period: '2019 - Present',
              achievements: ['Delivered premium consulting services to Fortune 100 clients', 'Maintained 98% client satisfaction rate', 'Led high-stakes strategic initiatives']
            }
          ]).map((exp, index) => (
            <div key={index} className="mb-6 pb-6 border-b border-rose-100 last:border-b-0">
              <div className="text-center mb-4">
                <h3 className="text-xl font-serif font-bold text-gray-900">{exp.title}</h3>
                <p className="text-lg text-rose-600 italic">{exp.company}</p>
                <span className="text-gray-500">{exp.period}</span>
              </div>
              <ul className="space-y-2">
                {exp.achievements.map((achievement, i) => (
                  <li key={i} className="text-gray-700 text-center">
                    <span className="inline-block w-2 h-2 bg-rose-300 rounded-full mr-2"></span>
                    {achievement}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Template 7: Technical
export const TechnicalTemplate = ({ resumeData }) => {
  return (
    <div className="bg-gray-900 p-8 max-w-4xl mx-auto text-green-400 font-mono" style={{ minHeight: '11in' }}>
      <div className="border border-green-400 rounded p-6">
        {/* Header */}
        <div className="mb-8 border-b border-green-400 pb-6">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h1 className="text-4xl font-bold mb-2 text-green-300">
                {resumeData?.name || 'root@system'}
              </h1>
              <p className="text-xl mb-3">$ role: {resumeData?.title || 'Software Engineer'}</p>
              <div className="space-y-1 text-sm">
                <div>$ email: {resumeData?.email || 'user@domain.com'}</div>
                <div>$ phone: {resumeData?.phone || '+1-555-123-4567'}</div>
                <div>$ location: {resumeData?.location || 'City, State'}</div>
              </div>
            </div>
            {resumeData?.photo && (
              <div className="ml-6">
                <img 
                  src={resumeData.photo} 
                  alt="Profile" 
                  className="w-24 h-24 rounded border-2 border-green-400 object-cover"
                />
              </div>
            )}
          </div>
        </div>

        {/* Summary */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4 text-green-300">
            # System.getCurrentUser().about()
          </h2>
          <div className="bg-black p-4 rounded border border-green-600">
            <p className="text-green-400 leading-relaxed">
              {resumeData?.summary || '// Passionate developer focused on clean code, scalable systems, and innovative solutions...'}
            </p>
          </div>
        </div>

        {/* Skills */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4 text-green-300">
            # Technical Stack
          </h2>
          <div className="grid grid-cols-2 gap-2">
            {(resumeData?.skills || ['JavaScript', 'React', 'Node.js', 'Python', 'Docker', 'AWS', 'MongoDB', 'Git']).map((skill, index) => (
              <div key={index} className="bg-black p-2 rounded border border-green-600">
                <span className="text-green-400">$ {skill}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Experience */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4 text-green-300">
            # Work History
          </h2>
          {(resumeData?.experience || [
            {
              title: 'Senior Software Engineer',
              company: 'TechCorp Inc.',
              period: '2020-present',
              achievements: ['Built scalable microservices architecture', 'Improved system performance by 300%', 'Led team of 5 developers']
            }
          ]).map((exp, index) => (
            <div key={index} className="mb-6 bg-black p-4 rounded border border-green-600">
              <div className="mb-3">
                <div className="text-green-300">
                  $ position: {exp.title}
                </div>
                <div className="text-green-400">
                  $ company: {exp.company}
                </div>
                <div className="text-gray-400">
                  $ duration: {exp.period}
                </div>
              </div>
              <div className="space-y-1">
                {exp.achievements.map((achievement, i) => (
                  <div key={i} className="text-green-400">
                    <span className="text-green-300">&gt;</span> {achievement}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Template 8: Academic
export const AcademicTemplate = ({ resumeData }) => {
  return (
    <div className="bg-white p-8 max-w-4xl mx-auto" style={{ minHeight: '11in' }}>
      {/* Header */}
      <div className="text-center mb-8 pb-6 border-b-2 border-blue-900">
        {resumeData?.photo && (
          <img 
            src={resumeData.photo} 
            alt="Profile" 
            className="w-24 h-24 rounded object-cover border-2 border-blue-900 mx-auto mb-4"
          />
        )}
        <h1 className="text-4xl font-serif text-blue-900 mb-2">{resumeData?.name || 'Dr. John Doe'}</h1>
        <p className="text-xl text-gray-700 mb-3">{resumeData?.title || 'Professor & Researcher'}</p>
        <div className="text-gray-600">
          <div>{resumeData?.email || 'professor@university.edu'}</div>
          <div>{resumeData?.phone || '(555) 123-4567'}</div>
          <div>{resumeData?.location || 'University City, State'}</div>
        </div>
      </div>

      {/* Research Interests */}
      <div className="mb-8">
        <h2 className="text-2xl font-serif text-blue-900 mb-4 border-b border-gray-300 pb-2">
          Research Summary
        </h2>
        <p className="text-gray-700 leading-relaxed text-justify">
          {resumeData?.summary || 'Distinguished academic with extensive research experience in multiple domains, committed to advancing knowledge through rigorous scholarship and innovative teaching methodologies...'}
        </p>
      </div>

      {/* Academic Positions */}
      <div className="mb-8">
        <h2 className="text-2xl font-serif text-blue-900 mb-4 border-b border-gray-300 pb-2">
          Academic Positions
        </h2>
        {(resumeData?.experience || [
          {
            title: 'Professor',
            company: 'University of Excellence',
            period: '2018 - Present',
            achievements: ['Published 25+ peer-reviewed papers', 'Secured $2M in research funding', 'Supervised 15 PhD students']
          }
        ]).map((exp, index) => (
          <div key={index} className="mb-6">
            <div className="flex justify-between items-baseline mb-2">
              <div>
                <h3 className="text-xl font-serif text-gray-900">{exp.title}</h3>
                <p className="text-lg text-blue-700 italic">{exp.company}</p>
              </div>
              <span className="text-gray-600">{exp.period}</span>
            </div>
            <ul className="ml-6 space-y-1">
              {exp.achievements.map((achievement, i) => (
                <li key={i} className="text-gray-700 list-disc">
                  {achievement}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Research Areas */}
      <div className="mb-8">
        <h2 className="text-2xl font-serif text-blue-900 mb-4 border-b border-gray-300 pb-2">
          Research Areas
        </h2>
        <div className="grid grid-cols-2 gap-4">
          {(resumeData?.skills || ['Machine Learning', 'Data Science', 'Statistics', 'Research Methods', 'Academic Writing', 'Grant Writing']).map((skill, index) => (
            <div key={index} className="flex items-center">
              <span className="w-2 h-2 bg-blue-900 rounded-full mr-3"></span>
              <span className="text-gray-700">{skill}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Template 9: Startup
export const StartupTemplate = ({ resumeData }) => {
  return (
    <div className="bg-gradient-to-br from-yellow-50 to-orange-50 p-8 max-w-4xl mx-auto" style={{ minHeight: '11in' }}>
      <div className="bg-white rounded-3xl shadow-xl p-8 border-4 border-orange-200">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {resumeData?.photo ? (
                <img 
                  src={resumeData.photo} 
                  alt="Profile" 
                  className="w-20 h-20 rounded-full object-cover border-4 border-orange-400 mr-6"
                />
              ) : (
                <div className="w-20 h-20 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 mr-6 flex items-center justify-center text-white text-2xl font-bold">
                  {(resumeData?.name || 'JD').charAt(0)}
                </div>
              )}
              <div>
                <h1 className="text-4xl font-bold text-gray-900 mb-1">{resumeData?.name || 'John Doe'}</h1>
                <p className="text-xl text-orange-600 font-semibold mb-2">{resumeData?.title || 'Startup Founder'}</p>
                <div className="flex space-x-4 text-gray-600 text-sm">
                  <span>{resumeData?.email || 'founder@startup.com'}</span>
                  <span>{resumeData?.phone || '(555) 123-4567'}</span>
                  <span>{resumeData?.location || 'Silicon Valley, CA'}</span>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-6 py-3 rounded-full font-bold text-lg">
              🚀 Innovator
            </div>
          </div>
        </div>

        {/* Mission Statement */}
        <div className="mb-8 bg-gradient-to-r from-yellow-50 to-orange-50 p-6 rounded-2xl border-l-4 border-orange-400">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
            <span className="mr-2">💡</span>
            Mission Statement
          </h2>
          <p className="text-gray-700 leading-relaxed text-lg">
            {resumeData?.summary || 'Passionate entrepreneur dedicated to disrupting traditional industries through innovative solutions and cutting-edge technology...'}
          </p>
        </div>

        {/* Core Skills */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
            <span className="mr-2">⚡</span>
            Core Skills
          </h2>
          <div className="flex flex-wrap gap-3">
            {(resumeData?.skills || ['Entrepreneurship', 'Product Development', 'Fundraising', 'Team Building', 'Growth Hacking', 'Innovation']).map((skill, index) => (
              <span key={index} className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-2 rounded-full font-semibold text-sm shadow-lg">
                {skill}
              </span>
            ))}
          </div>
        </div>

        {/* Experience */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
            <span className="mr-2">📈</span>
            Startup Journey
          </h2>
          {(resumeData?.experience || [
            {
              title: 'Co-Founder & CEO',
              company: 'DisruptTech Startup',
              period: '2020 - Present',
              achievements: ['Raised $5M in Series A funding', 'Grew team from 2 to 50 employees', 'Achieved $1M ARR in 18 months']
            }
          ]).map((exp, index) => (
            <div key={index} className="mb-6 bg-white p-6 rounded-2xl border-2 border-orange-200 shadow-lg">
              <div className="flex justify-between items-center mb-3">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{exp.title}</h3>
                  <p className="text-lg text-orange-600 font-semibold">{exp.company}</p>
                </div>
                <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full font-medium text-sm">{exp.period}</span>
              </div>
              <ul className="space-y-2">
                {exp.achievements.map((achievement, i) => (
                  <li key={i} className="text-gray-700 flex items-start">
                    <span className="text-orange-500 mr-2 text-lg">→</span>
                    {achievement}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const TemplateSelector = ({ selectedTemplate, onTemplateSelect }) => {
  const templates = [
    { id: 'professional', name: 'Professional', component: ProfessionalTemplate, description: 'Classic corporate design' },
    { id: 'creative', name: 'Creative', component: CreativeTemplate, description: 'Modern and visually appealing' },
    { id: 'minimalist', name: 'Minimalist', component: MinimalistTemplate, description: 'Clean and simple layout' },
    { id: 'executive', name: 'Executive', component: ExecutiveTemplate, description: 'Bold leadership-focused design' },
    { id: 'executive', name: 'Executive', component: ExecutiveTemplate, description: 'Bold leadership-focused design' },
    { id: 'executive', name: 'Executive', component: ExecutiveTemplate, description: 'Bold leadership-focused design' },
    { id: 'modern', name: 'Modern', component: ModernTemplate, description: 'Contemporary gradient design' },
    { id: 'elegant', name: 'Elegant', component: ElegantTemplate, description: 'Sophisticated and refined' },
    { id: 'technical', name: 'Technical', component: TechnicalTemplate, description: 'Developer-focused terminal style' },
    { id: 'academic', name: 'Academic', component: AcademicTemplate, description: 'Scholarly and professional' },
    { id: 'startup', name: 'Startup', component: StartupTemplate, description: 'Energetic entrepreneur design' }
  ];

  return (
    <div className="space-y-6">
      <h3 className="text-2xl font-bold text-gray-900 mb-6">Choose Your Template</h3>
      
      <div className="grid md:grid-cols-3 gap-6">
        {templates.map((template) => (
          <motion.div
            key={template.id}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => onTemplateSelect(template.id)}
            className={`cursor-pointer border-2 rounded-2xl p-4 transition-all ${
              selectedTemplate === template.id
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="mb-4">
              <div className="bg-gray-100 rounded-lg h-32 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg mx-auto mb-2 flex items-center justify-center text-white text-sm font-bold">
                    {template.name.charAt(0)}
                  </div>
                  <p className="text-xs text-gray-600">Preview</p>
                </div>
              </div>
            </div>
            
            <h4 className="font-semibold text-gray-900 mb-2">{template.name}</h4>
            <p className="text-sm text-gray-600">{template.description}</p>
            
            {selectedTemplate === template.id && (
              <div className="mt-3 flex items-center text-blue-600 text-sm">
                <span className="mr-2">✓</span>
                Selected
              </div>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );
};